import { useEffect, useState } from "react";
import { Position } from "@xyflow/react";
import type * as Y from "yjs";
import {
  buildConductorToCableIndex,
  type Cable,
  type Conductor,
  type ConductorId,
  type Device,
  type DeviceOnSheet,
  type DomainMaps,
  LOCAL_ORIGIN,
  listConductors,
  listDeviceLayoutsForSheet,
  listDevices,
  listTerminals,
  type SheetId,
  type Terminal,
  type TerminalId,
  validateConnection,
} from "@electro-planner/domain";
import { getSymbol, type SymbolDefinition, type TerminalDef } from "@electro-planner/symbols";
import { SYMBOL_SIZE } from "../constants.ts";
import {
  computeHops,
  computeOrthogonalPath,
  type EdgePathInput,
  type Point,
} from "../edges/routing.ts";
import {
  computeJunctions,
  isPointOnJunction,
  junctionKeySet,
  type Junction,
} from "../edges/junctions.ts";
import type { ElectricalSymbolNodeType } from "../nodes/ElectricalSymbolNode.tsx";
import type { OrthogonalWireEdgeType } from "../edges/OrthogonalWireEdge.tsx";

export type ProjectionResult = {
  nodes: ElectricalSymbolNodeType[];
  edges: OrthogonalWireEdgeType[];
  junctions: Junction[];
};

function project(maps: DomainMaps, sheetId: SheetId): ProjectionResult {
  const devices = listDevices(maps);
  const terminals = listTerminals(maps);
  const layouts = listDeviceLayoutsForSheet(maps, sheetId);
  const conductors = listConductors(maps);
  const cableByConductor = buildConductorToCableIndex(maps);

  const deviceById = new Map<string, Device>(devices.map((d) => [d.id, d]));
  const layoutByDevice = new Map<string, DeviceOnSheet>(
    layouts.map((l) => [l.deviceId, l]),
  );
  const terminalById = new Map<TerminalId, Terminal>(
    terminals.map((t) => [t.id, t]),
  );

  const nodes: ElectricalSymbolNodeType[] = [];
  for (const layout of layouts) {
    const device = deviceById.get(layout.deviceId);
    if (!device) continue;
    const symbolId =
      (typeof device.attributes["symbolId"] === "string"
        ? (device.attributes["symbolId"] as string)
        : undefined) ?? "";
    const symbol = getSymbol(symbolId);
    if (!symbol) continue;
    nodes.push({
      id: device.id,
      type: "electricalSymbol",
      position: { x: layout.x, y: layout.y },
      data: {
        symbolId,
        designation: device.designation,
        rotation: layout.rotation,
        symbol,
      },
    });
  }

  const edges: OrthogonalWireEdgeType[] = [];
  for (const cond of conductors) {
    const cable = cableByConductor.get(cond.id as ConductorId);
    const edge = projectConductor(cond, terminalById, layoutByDevice, cable);
    if (edge) edges.push(edge);
  }

  // Line breaking: beräkna orthogonala vägar på nytt i flow-koordinater,
  // detektera alla korsningar och attacha hop-punkter till den "större" edge:n.
  const symbolCache = new Map<string, SymbolDefinition>();
  const symbolFor = (device: Device): SymbolDefinition | null => {
    const id = device.attributes["symbolId"];
    if (typeof id !== "string") return null;
    const cached = symbolCache.get(id);
    if (cached) return cached;
    const fresh = getSymbol(id);
    if (fresh) symbolCache.set(id, fresh);
    return fresh ?? null;
  };

  const edgePaths: EdgePathInput[] = [];
  for (const edge of edges) {
    const sourceDevice = deviceById.get(edge.source);
    const targetDevice = deviceById.get(edge.target);
    if (!sourceDevice || !targetDevice) continue;
    const sourceSymbol = symbolFor(sourceDevice);
    const targetSymbol = symbolFor(targetDevice);
    if (!sourceSymbol || !targetSymbol) continue;
    const sourceLayout = layoutByDevice.get(edge.source);
    const targetLayout = layoutByDevice.get(edge.target);
    if (!sourceLayout || !targetLayout) continue;
    const sourceTerm = sourceSymbol.terminals.find(
      (t) => t.id === edge.sourceHandle,
    );
    const targetTerm = targetSymbol.terminals.find(
      (t) => t.id === edge.targetHandle,
    );
    if (!sourceTerm || !targetTerm) continue;
    const from = handleAbsolutePosition(sourceLayout, sourceTerm);
    const to = handleAbsolutePosition(targetLayout, targetTerm);
    const waypoints = computeOrthogonalPath(
      from.point,
      to.point,
      from.sidePosition,
      to.sidePosition,
    );
    edgePaths.push({ id: edge.id, waypoints });
  }

  const hopsByEdge = computeHops(edgePaths);
  const junctions = computeJunctions(edgePaths);
  // En faktisk anslutning (junction) ska alltid vinna över en korsnings-båge
  // (hop) på samma punkt — annars ser det ut som om ledningen bara passerar.
  const junctionKeys = junctionKeySet(junctions);
  if (hopsByEdge.size > 0) {
    for (const edge of edges) {
      const hops = hopsByEdge.get(edge.id);
      if (!hops || hops.length === 0) continue;
      const filtered =
        junctionKeys.size > 0
          ? hops.filter((h) => !isPointOnJunction(h, junctionKeys))
          : hops;
      if (filtered.length > 0) {
        edge.data = { ...edge.data, hops: filtered };
      }
    }
  }

  return { nodes, edges, junctions };
}

function handleAbsolutePosition(
  layout: DeviceOnSheet,
  terminal: TerminalDef,
): { point: Point; sidePosition: Position } {
  const half = SYMBOL_SIZE / 2;
  const o = terminal.offset;
  switch (terminal.position) {
    case "top":
      return {
        point: { x: layout.x - half + SYMBOL_SIZE * o, y: layout.y - half },
        sidePosition: Position.Top,
      };
    case "bottom":
      return {
        point: { x: layout.x - half + SYMBOL_SIZE * o, y: layout.y + half },
        sidePosition: Position.Bottom,
      };
    case "left":
      return {
        point: { x: layout.x - half, y: layout.y - half + SYMBOL_SIZE * o },
        sidePosition: Position.Left,
      };
    case "right":
      return {
        point: { x: layout.x + half, y: layout.y - half + SYMBOL_SIZE * o },
        sidePosition: Position.Right,
      };
  }
}

function projectConductor(
  cond: Conductor,
  terminalById: Map<TerminalId, Terminal>,
  layoutByDevice: Map<string, DeviceOnSheet>,
  cable: Cable | undefined,
): OrthogonalWireEdgeType | null {
  const fromT = terminalById.get(cond.fromTerminal);
  const toT = terminalById.get(cond.toTerminal);
  if (!fromT || !toT) return null;
  const fromLayout = layoutByDevice.get(fromT.deviceId);
  const toLayout = layoutByDevice.get(toT.deviceId);
  if (!fromLayout || !toLayout) return null;

  // Återanvänd domänens valideringsregler för att markera edges som har en
  // mismatchad koppling (L1 mot L2, phase mot signal, …). Hårda blockeringar
  // (earth mot phase) fångas redan av isValidConnection innan conductor skapas
  // så här ser vi bara "warning"-fallen.
  const validation = validateConnection(fromT, toT);
  const severity = validation.severity === "warning" ? "warning" : undefined;
  const reason = severity ? validation.reason : undefined;

  return {
    id: cond.id,
    type: "orthogonalWire",
    source: fromT.deviceId,
    target: toT.deviceId,
    sourceHandle: fromT.symbolTerminalId,
    targetHandle: toT.symbolTerminalId,
    data: {
      area: cond.attributes.area,
      color: cond.attributes.color,
      circuitLabel: cond.attributes.circuitLabel,
      cableDesignation: cable?.designation,
      cableId: cable?.id,
      cableType: cable?.type,
      severity,
      reason,
    },
  };
}

/**
 * Observerar DomainMaps och re-projektar vid varje ändring.
 * Allt flödar EN väg: Yjs → React state → React Flow. Mutationer sker
 * i useDomainMutations och wrappas i LOCAL_ORIGIN; observer:n applicerar
 * re-projekt även för LOCAL-ändringar så React Flow-state och Yjs förblir synk.
 */
export function useReactFlowProjection(
  doc: Y.Doc,
  maps: DomainMaps,
  sheetId: SheetId,
): ProjectionResult {
  const [snapshot, setSnapshot] = useState<ProjectionResult>(() =>
    project(maps, sheetId),
  );

  useEffect(() => {
    setSnapshot(project(maps, sheetId));

    const handler = (_events: unknown, transaction: Y.Transaction) => {
      // Re-projektar oavsett origin. LOCAL_ORIGIN är viktig först när vi måste
      // filtrera bort egna updates för att undvika dubbel-write mot en extern
      // sync (Hocuspocus) — det kommer i Fas 4. Här är re-project idempotent.
      void transaction;
      setSnapshot(project(maps, sheetId));
    };

    maps.devices.observeDeep(handler);
    maps.terminals.observeDeep(handler);
    maps.conductors.observeDeep(handler);
    maps.deviceLayout.observeDeep(handler);
    maps.conductorRouting.observeDeep(handler);
    maps.cables.observeDeep(handler);

    return () => {
      maps.devices.unobserveDeep(handler);
      maps.terminals.unobserveDeep(handler);
      maps.conductors.unobserveDeep(handler);
      maps.deviceLayout.unobserveDeep(handler);
      maps.conductorRouting.unobserveDeep(handler);
      maps.cables.unobserveDeep(handler);
    };
  }, [doc, maps, sheetId]);

  return snapshot;
}

// Håller referensen till LOCAL_ORIGIN kallt i typ-sfären.
export const _LOCAL_ORIGIN: symbol = LOCAL_ORIGIN;
